// ============================================
// SETTINGS PAGE
// ============================================

import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  User,
  Bell,
  Shield,
  CreditCard,
  Trash2,
  Save,
  Key,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
} from 'lucide-react';
import { PageHeader } from '@/components';
import { authApi } from '@/services/api';
import { useAuthStore } from '@/store/auth';
import { useNotificationSettings, useUpdateNotificationSettings, useDeleteAccount } from '@/hooks';
import { cn } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/settings/')({
  component: SettingsPage,
});

type SettingsTab = 'profile' | 'notifications' | 'security';

// LocalStorage key for profile data
const PROFILE_STORAGE_KEY = 'company-profile-data';

function SettingsPage() {
  const { t } = useTranslation('panel');
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');

  const tabs: { id: SettingsTab; label: string; icon: typeof User }[] = [
    { id: 'profile', label: t('settings.tabs.profile'), icon: User },
    { id: 'notifications', label: t('settings.tabs.notifications'), icon: Bell },
    { id: 'security', label: t('settings.tabs.security'), icon: Shield },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('settings.title')}
        description={t('settings.description')}
      />

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="w-full lg:w-64 flex-shrink-0">
          <nav className="bg-white rounded-maritime border border-gray-200 overflow-hidden">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'bg-maritime-50 text-maritime-700 border-l-2 border-maritime-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                )}
              >
                <tab.icon className="h-5 w-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1">
          {activeTab === 'profile' && <ProfileSettings />}
          {activeTab === 'notifications' && <NotificationSettings />}
          {activeTab === 'security' && <SecuritySettings />}
        </div>
      </div>
    </div>
  );
}

// ============================================
// PROFILE SETTINGS
// ============================================
interface ProfileData {
  firstName: string;
  lastName: string;
  phone: string;
  position: string;
}

function ProfileSettings() {
  const { t } = useTranslation('panel');
  const { user } = useAuthStore();
  
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [position, setPosition] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(PROFILE_STORAGE_KEY);
      if (saved) {
        const data: ProfileData = JSON.parse(saved);
        setFirstName(data.firstName || '');
        setLastName(data.lastName || '');
        setPhone(data.phone || '');
        setPosition(data.position || '');
      }
    } catch (e) {
      // Failed to load profile data
    }
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      const profileData: ProfileData = {
        firstName,
        lastName,
        phone,
        position,
      };
      
      localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profileData));
      
      toast.success(t('settings.profile.saveSuccess'));
    } catch (error: any) {
      toast.error(error?.message || t('settings.profile.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-maritime border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('settings.profile.title')}</h3>
      
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings.profile.firstName')}
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full h-10 px-3 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
              placeholder={t('settings.profile.firstNamePlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings.profile.lastName')}
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full h-10 px-3 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
              placeholder={t('settings.profile.lastNamePlaceholder')}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('settings.profile.email')}
          </label>
          <input
            type="email"
            value={user?.email || ''}
            disabled
            className="w-full h-10 px-3 border border-gray-300 rounded-maritime bg-gray-50 text-gray-500 cursor-not-allowed"
            placeholder="ornek@sirket.com"
          />
          <p className="text-xs text-gray-400 mt-1">{t('settings.profile.emailHint')}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('settings.profile.phone')}
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+90 5XX XXX XX XX"
            className="w-full h-10 px-3 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('settings.profile.position')}
          </label>
          <input
            type="text"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            className="w-full h-10 px-3 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
            placeholder={t('settings.profile.positionPlaceholder')}
          />
        </div>

        <div className="pt-4 border-t border-gray-200">
          <button 
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700 transition-colors disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('settings.profile.saving')}
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {t('settings.profile.save')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// NOTIFICATION SETTINGS
// ============================================
function NotificationSettings() {
  const { t } = useTranslation('panel');
  const { data: settingsResponse, isLoading, refetch } = useNotificationSettings();
  const updateSettings = useUpdateNotificationSettings();

  const handleToggle = (key: string, value: boolean) => {
    updateSettings.mutate({ [key]: value }, {
      onSuccess: () => {
        refetch();
      }
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-maritime border border-gray-200 p-6">
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-6 h-6 animate-spin text-maritime-600" />
        </div>
      </div>
    );
  }

  const rawSettings = settingsResponse as any;
  const settingsData = rawSettings?.data || rawSettings || {};
  
  const currentSettings = {
    emailNotifications: settingsData.emailNotifications ?? true,
    pushNotifications: settingsData.pushNotifications ?? true,
    jobAlerts: settingsData.jobAlerts ?? true,
    applicationUpdates: settingsData.applicationUpdates ?? true,
    marketingEmails: settingsData.marketingEmails ?? true,
  };

  return (
    <div className="bg-white rounded-maritime border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('settings.notifications.title')}</h3>
      
      <div className="space-y-6">
        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div>
            <p className="font-medium text-gray-900">{t('settings.notifications.email.title')}</p>
            <p className="text-sm text-gray-500">{t('settings.notifications.email.description')}</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={currentSettings.emailNotifications}
              onChange={(e) => handleToggle('emailNotifications', e.target.checked)}
              className="sr-only peer"
              disabled={updateSettings.isPending}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-maritime-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-maritime-600 peer-disabled:opacity-50"></div>
          </label>
        </div>

        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div>
            <p className="font-medium text-gray-900">{t('settings.notifications.push.title')}</p>
            <p className="text-sm text-gray-500">{t('settings.notifications.push.description')}</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={currentSettings.pushNotifications}
              onChange={(e) => handleToggle('pushNotifications', e.target.checked)}
              className="sr-only peer"
              disabled={updateSettings.isPending}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-maritime-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-maritime-600 peer-disabled:opacity-50"></div>
          </label>
        </div>

        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div>
            <p className="font-medium text-gray-900">{t('settings.notifications.applicationUpdates.title')}</p>
            <p className="text-sm text-gray-500">{t('settings.notifications.applicationUpdates.description')}</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={currentSettings.applicationUpdates}
              onChange={(e) => handleToggle('applicationUpdates', e.target.checked)}
              className="sr-only peer"
              disabled={updateSettings.isPending}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-maritime-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-maritime-600 peer-disabled:opacity-50"></div>
          </label>
        </div>

        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div>
            <p className="font-medium text-gray-900">{t('settings.notifications.jobAlerts.title')}</p>
            <p className="text-sm text-gray-500">{t('settings.notifications.jobAlerts.description')}</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={currentSettings.jobAlerts}
              onChange={(e) => handleToggle('jobAlerts', e.target.checked)}
              className="sr-only peer"
              disabled={updateSettings.isPending}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-maritime-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-maritime-600 peer-disabled:opacity-50"></div>
          </label>
        </div>

        <div className="flex items-center justify-between py-3">
          <div>
            <p className="font-medium text-gray-900">{t('settings.notifications.marketing.title')}</p>
            <p className="text-sm text-gray-500">{t('settings.notifications.marketing.description')}</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={currentSettings.marketingEmails}
              onChange={(e) => handleToggle('marketingEmails', e.target.checked)}
              className="sr-only peer"
              disabled={updateSettings.isPending}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-maritime-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-maritime-600 peer-disabled:opacity-50"></div>
          </label>
        </div>
      </div>

      {updateSettings.isPending && (
        <p className="text-xs text-gray-500 mt-4 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          {t('settings.notifications.saving')}
        </p>
      )}
    </div>
  );
}

// ============================================
// SECURITY SETTINGS
// ============================================
function SecuritySettings() {
  const { t } = useTranslation('panel');
  const { user } = useAuthStore();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const deleteAccountMutation = useDeleteAccount();

  // Delete Account Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
  };
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string; confirmPassword: string }) =>
      authApi.changePassword(data),
    onSuccess: () => {
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success(t('settings.security.changeSuccess'));
      setTimeout(() => setPasswordSuccess(false), 3000);
    },
    onError: (error: any) => {
      const msg = error?.message || t('settings.security.changeError');
      setPasswordError(msg);
      toast.error(msg);
    },
  });

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (newPassword.length < 8) {
      setPasswordError(t('settings.security.validation.min8'));
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setPasswordError(t('settings.security.validation.uppercase'));
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setPasswordError(t('settings.security.validation.lowercase'));
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setPasswordError(t('settings.security.validation.number'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t('settings.security.validation.mismatch'));
      return;
    }

    changePasswordMutation.mutate({ currentPassword, newPassword, confirmPassword });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-maritime border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('settings.security.title')}</h3>
        
        {passwordSuccess && (
          <div className="mb-4 p-3 rounded-maritime bg-green-50 border border-green-200 flex items-center gap-2 text-green-700 text-sm">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            <span>{t('settings.security.changeSuccess')}</span>
          </div>
        )}

        {passwordError && (
          <div className="mb-4 p-3 rounded-maritime bg-red-50 border border-red-200 flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{passwordError}</span>
          </div>
        )}
        
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings.security.currentPassword')}
            </label>
            <div className="relative">
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full h-10 px-3 pr-10 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              >
                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings.security.newPassword')}
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full h-10 px-3 pr-10 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings.security.confirmPassword')}
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full h-10 px-3 pr-10 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          <div className="p-3 rounded-maritime bg-gray-50 border border-gray-200">
            <p className="text-xs font-medium text-gray-700 mb-2">{t('settings.security.requirements.title')}</p>
            <ul className="text-xs text-gray-600 space-y-1">
              <li className={newPassword.length >= 8 ? 'text-green-600' : ''}>• {t('settings.security.requirements.min8')}</li>
              <li className={/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}>• {t('settings.security.requirements.uppercase')}</li>
              <li className={/[a-z]/.test(newPassword) ? 'text-green-600' : ''}>• {t('settings.security.requirements.lowercase')}</li>
              <li className={/[0-9]/.test(newPassword) ? 'text-green-600' : ''}>• {t('settings.security.requirements.number')}</li>
            </ul>
          </div>

          <button 
            type="submit"
            disabled={changePasswordMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700 transition-colors disabled:opacity-50"
          >
            {changePasswordMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('settings.security.updating')}
              </>
            ) : (
              <>
                <Key className="h-4 w-4" />
                {t('settings.security.updateButton')}
              </>
            )}
          </button>
        </form>
      </div>

      {/* Danger Zone */}
      <div className="bg-red-50/10 rounded-maritime border border-red-200 p-6 mt-6">
        <div className="flex items-center gap-3 mb-4">
          <Trash2 className="w-5 h-5 text-red-600" />
          <h3 className="text-lg font-semibold text-red-950">Tehlikeli Bölge</h3>
        </div>
        
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-red-950">Şirket Hesabını Kalıcı Olarak Sil</p>
            <p className="text-sm text-red-700 max-w-xl mt-1">
              Hesabınızı sildiğinizde, şirket profiliniz, tüm aktif ve pasif iş ilanlarınız ile bunlara yapılmış olan tüm başvurular kalıcı olarak silinir. Bu işlem geri alınamaz.
            </p>
          </div>
          <button
            onClick={handleDeleteAccount}
            disabled={deleteAccountMutation.isPending}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors shadow-sm disabled:opacity-50 flex-shrink-0 w-full sm:w-auto text-sm"
          >
            {deleteAccountMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Siliniyor...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Hesabımı Sil
              </>
            )}
          </button>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <DeleteAccountModal
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => deleteAccountMutation.mutate()}
          registeredEmail={user?.email || ''}
          isPending={deleteAccountMutation.isPending}
        />
      )}
    </div>
  );
}

// ============================================
// BILLING SETTINGS
// ============================================
export function BillingSettings() {
  const { t } = useTranslation('panel');

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-maritime border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('settings.billing.currentPlan')}</h3>
        
        <div className="flex items-center justify-between p-4 bg-green-50 rounded-maritime border border-green-200">
          <div>
            <p className="font-semibold text-green-900">{t('settings.billing.unlimited')}</p>
            <p className="text-sm text-green-700">{t('settings.billing.unlimitedDesc')}</p>
          </div>
          <span className="px-3 py-1 text-sm font-medium bg-green-600 text-white rounded-full">
            {t('settings.billing.active')}
          </span>
        </div>
      </div>

      <div className="bg-white rounded-maritime border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('settings.billing.paymentMethod')}</h3>
        
        <p className="text-sm text-gray-500 mb-4">
          {t('settings.billing.paymentComingSoon')}
        </p>
        
        <button 
          disabled
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-400 rounded-maritime cursor-not-allowed"
          title={t('settings.billing.paymentNotActive')}
        >
          <CreditCard className="h-4 w-4" />
          {t('settings.billing.addPayment')}
        </button>
      </div>

      <div className="bg-white rounded-maritime border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('settings.billing.invoiceHistory')}</h3>
        
        <p className="text-sm text-gray-500">
          {t('settings.billing.noInvoices')}
        </p>
      </div>
    </div>
  );
}

// ============================================
// DELETE ACCOUNT MODAL
// ============================================
function DeleteAccountModal({ onClose, onConfirm, registeredEmail, isPending }: { 
  onClose: () => void; 
  onConfirm: () => void; 
  registeredEmail: string;
  isPending: boolean;
}) {
  const [emailInput, setEmailInput] = useState('');
  const isEmailMatch = emailInput.trim().toLowerCase() === registeredEmail.toLowerCase();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEmailMatch && !isPending) {
      onConfirm();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={onClose}
      />
      
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 border border-slate-100">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Hesabı Sil</h2>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="bg-red-50 border border-red-100 rounded-xl p-4 flex gap-3 text-red-800 text-sm mb-6">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-semibold">Bu işlem geri alınamaz!</p>
            <p className="leading-relaxed">Hesabınızla birlikte tüm şirket verileriniz, yayınladığınız iş ilanları ve aday başvuruları kalıcı olarak silinecektir.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Lütfen onaylamak için kayıtlı e-posta adresinizi (<span className="font-mono text-slate-900 select-all">{registeredEmail}</span>) yazın:
            </label>
            <input
              type="email"
              required
              autoFocus
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all text-sm font-mono"
              placeholder="e-posta@adresiniz.com"
              disabled={isPending}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-colors text-sm"
              disabled={isPending}
            >
              İptal
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
              disabled={!isEmailMatch || isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Siliniyor...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Hesabımı Sil
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
